/**
 * Seam between a restaurant-settings API and the local persisted store.
 *
 * - false (default, and the only working value today): everything lives in
 *   `useRestaurantSettingsStore`, no network calls. It survives a reload because
 *   the store persists; nothing is shared between devices or staff members, and
 *   nothing reaches the guest-facing app, the published website or an invoice.
 * - true: `useRestaurantSettingsHydration` would GET the document below and every
 *   per-tab save would mirror back. The components do not change between the two
 *   modes — they read the store and call the hook, both of which honour this flag.
 *   Same arrangement as the loyalty programme, the floor planner, the roster and
 *   the website builder.
 *
 * WHY IT IS OFF: the columns do not exist. `restaurant.entity.ts` holds `name`,
 * `currency`, `defaultLanguageCodeId`, logo and background urls with their
 * versions, `venueName`, `venueWebsiteText`, `venueWebsiteUrl`, four colours and
 * `qrCodeSize` — and nothing else. There is no legal name, no NIP, no REGON, no
 * address, no place id, no timezone, no opening hours and no social links.
 * `restaurants.controller.ts` has no PATCH or PUT on `/restaurants/:restaurantId`
 * at all: the only writes are languages, the mobile-app theme and the QR size.
 * This is not "not wired up yet", it is "there is nowhere to put it".
 *
 * WHAT THE BACKEND WOULD NEED, in the order it becomes useful:
 *
 * 1. The document itself, addressed the way the floor plan is:
 *    GET/PUT `/restaurants/{restaurantId}/settings`, whole-document PUT, which is
 *    the shape this store already has. ACCESS CONTROL NEEDS NO CHANGE:
 *    `PermissionEnum.UPDATE_RESTAURANTS` is already seeded for OWNER in
 *    `seeds.role-permission.repository.ts` and is referenced by no guard
 *    anywhere — the endpoint only has to declare it.
 *
 * 2. The checksums in `shared/utils/polishBusinessIds` have to be repeated
 *    server-side — a check that runs in a browser is a courtesy. Phone numbers
 *    want normalising against `DEFAULTS.phoneNumberRegion` ('PL') on the way in.
 *
 * 3. These facts are read by more than this panel: the website renderer
 *    snapshots them into its document, and the guest app will want the address
 *    and the opening hours. That read has no session, so it needs a public route
 *    the way the mobile-app theme already has one
 *    (`/public/restaurants/{restaurantId}/mobile-app`).
 *
 * WHO OWNS THE VENUE FACTS: this store does. The website builder's
 * `document.venue` is a SNAPSHOT, copied at build time through
 * `adapters/toSiteSettings.ts`, for the same reason `reservationRooms` is a copy
 * of the floor plan — a published page has no session to fetch anything with, and
 * an address corrected next month must not silently rewrite a page published last
 * month. One writer here, one copy there, one adapter between them.
 */
export const RESTAURANT_SETTINGS_USE_API = false

/** Backend path, relative to `/restaurants/{restaurantId}`. */
export const RESTAURANT_SETTINGS_ENDPOINT = '/settings'

/**
 * Zones offered for the venue's address.
 *
 * A short list, not the IANA database: the full set needs
 * `Intl.supportedValuesOf`, which is ES2022 and outside this repo's tsconfig
 * `lib` (ES2020), and a 400-entry dropdown is not what a venue with one address
 * needs. The current value is seeded from the browser
 * (`Intl.DateTimeFormat().resolvedOptions().timeZone`) rather than asked of
 * Google: the Time Zone API is a separate, separately billed product and is not
 * enabled on the project.
 */
export const TIMEZONE_OPTIONS = [
  'Europe/Warsaw',
  'Europe/Berlin',
  'Europe/Prague',
  'Europe/Vilnius',
  'Europe/Kyiv',
  'Europe/London',
  'Europe/Dublin',
  'Europe/Madrid',
  'Europe/Rome',
  'Europe/Stockholm'
] as const

/**
 * The language `tagline` and `description` are typed under until the panel can
 * offer tabs per language.
 *
 * 'pl' matches the backend's own `DEFAULTS.languageCode`. This is a fourth
 * answer to "what language is this venue in?", alongside
 * `restaurant.defaultLanguageCodeId`, the website document's `defaultLanguage`
 * and the interface language — which is one too many. The endpoint should drop it
 * and read `defaultLanguageCodeId`, and `GET /restaurants/{id}/languages` is
 * already preloaded in `shared/hooks/usePreloadQueries` to supply the tabs.
 */
export const DEFAULT_CONTENT_LANGUAGE = 'pl'

/** Bumped together with a `migrate` branch in the store. 1: initial shape. */
export const RESTAURANT_SETTINGS_SCHEMA_VERSION = 1

/** Seven rows, weekday 0 = Monday, matching how the roster counts days. */
export const WEEKDAY_COUNT = 7
